import { cn } from '../lib/utils';

// Tokens shared by Button and IconButton — variant × size axes.
// Visual reference: /playground/buttons.

export type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
export type Size = 'sm' | 'md' | 'lg' | 'xl';

// Filled variants (primary/destructive) hover via a stacked alpha image so the
// underlying bg-color stays untouched. Neutral variants swap bg-color directly.
// Tailwind classes are written literally because JIT can't resolve `${var}:hover`.
export const variantStyles: Record<Variant, string> = {
  primary: cn(
    'text-white shadow-xs',
    'bg-highlight-dark',
    'ring-1 ring-inset ring-white/40',
    'not-disabled:not-aria-disabled:hover:bg-[image:linear-gradient(var(--color-primary-alpha-10),var(--color-primary-alpha-10))]',
  ),
  secondary: cn(
    'bg-primary-5 text-primary-100',
    'ring-1 ring-inset ring-primary-alpha-5',
    'not-disabled:not-aria-disabled:hover:bg-primary-10',
  ),
  outline: cn(
    'bg-white text-primary-100',
    'ring-1 ring-inset ring-primary-10',
    'not-disabled:not-aria-disabled:hover:bg-primary-3',
  ),
  ghost: 'bg-transparent text-primary-70 not-disabled:not-aria-disabled:hover:bg-primary-5',
  destructive: cn(
    'bg-status-danger-light text-status-danger',
    'ring-1 ring-inset ring-status-danger/10',
    'not-disabled:not-aria-disabled:hover:bg-status-danger/15',
  ),
};

export const iconSize: Record<Size, string> = {
  sm: 'text-sm',
  md: 'text-m',
  lg: 'text-ml',
  xl: 'text-[20px]',
};

// Press feedback scales the inner content span only, so adjacent buttons stay
// stable. Anchor: `data-slot="content"` on the wrapping span.
export const pressContent =
  'motion-safe:not-disabled:not-aria-disabled:active:[&>[data-slot=content]]:scale-[0.98]';

// `leading-none` keeps icon and text glyph centers aligned during the press scale.
export const contentLayer = cn(
  'inline-flex items-center justify-center gap-1 leading-none',
  'motion-safe:transition-transform motion-safe:duration-100 motion-safe:ease-out',
);

// `relative isolate overflow-hidden` lets the loading bar sit behind content
// at z-index -1, clipped to the radius. `touch-manipulation` kills the 300ms
// double-tap-zoom delay on iOS.
export const interactiveBase = cn(
  'relative isolate inline-flex shrink-0 cursor-pointer items-center justify-center overflow-hidden',
  'touch-manipulation',
  'outline-none transition-[background-color,color,opacity,box-shadow] duration-100 ease-out',
  'focus-visible:ring-2 focus-visible:ring-primary-20/50',
  pressContent,
);

// Applied only when the button is permanently disabled — kept off during
// loading so the bar overlay stays at full opacity.
export const disabledStyles =
  'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

export const loadingBar = cn(
  'pointer-events-none absolute inset-y-0 left-0 w-0',
  'animate-button-loading-bar',
);

export const loadingBarBg: Record<Variant, string> = {
  primary: 'bg-white/20',
  secondary: 'bg-primary-alpha-10',
  outline: 'bg-primary-alpha-10',
  ghost: 'bg-primary-alpha-5',
  destructive: 'bg-status-danger/10',
};

export const loadingStyles = 'cursor-progress';
